import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useContext
} from 'react';
import { string, bool, number, shape } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductImage,
  PodFooter,
  PodSection,
  PodSpacer,
} from '@thd-olt-component-react/product-pod';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { extend } from '@thd-nucleus/data-sources';
import { Price } from '@thd-olt-component-react/price';
import { triggerNewRelic } from '../../utils/helpers';
import { newRelicConstants } from '../../constants';
import './SponsoredProductPod.scss';

const SponsoredProductPod = ({
  product,
  storeId,
  position,
  schema,
  pageId,
  noATCFulfillment,
  targetingSource,
}) => {
  const [beacon, setBeacon] = useState('');
  const [linkBeacon, setLinkBeacon] = useState('');
  const [isProductClickHandlerAttached, setIsProductClickHandlerAttached] = useState(false);
  const [atcBeacon, setAtcBeacon] = useState('');
  const [atcButton, setAtcButton] = useState();
  const [isATCButtonCLickHandlerAttached, setIsATCButtonCLickHandlerAttached] = useState(false);

  const experienceContext = useContext(ExperienceContext);

  const isMobile = useMemo(() => (experienceContext.channel === 'mobile'), [experienceContext.channel]);

  useEffect(() => {
    const protocolSubstring = 'https:';
    let { onClickBeacon } = product;

    if (onClickBeacon.startsWith(protocolSubstring)) {
      onClickBeacon = onClickBeacon.slice(protocolSubstring.length);
    }

    setBeacon(onClickBeacon);
  }, [product.onClickBeacon]);

  useEffect(() => {
    const merchRec = `MERCH=REC-_-sp-_-${schema}-_-${position}-_-n/a-_-${pageId}-_-n/a-_-n/a-_-n/a`;
    /* eslint-disable-next-line */
    setLinkBeacon(`${beacon}${location.protocol}//${location.host}${product.canonicalURL}?${merchRec}&ITC=${product.ITC}`);
  }, [product, position, schema, pageId, beacon]);

  const handleProductClick = useCallback((evt) => {
    if (linkBeacon) {
      triggerNewRelic(`${newRelicConstants.CAROUSEL_PLA}-${targetingSource}`, newRelicConstants.CLICK);
      evt.preventDefault();
      window.location = linkBeacon;
    }
  }, [linkBeacon]);

  useEffect(() => {
    if (!isProductClickHandlerAttached) {
      const productLinksSelector = `[data-product-id="${product.id}"] a[href*="${product.id}"]`;
      const productLinks = document.querySelectorAll(productLinksSelector);

      if (productLinks.length) {
        productLinks.forEach((link) => {
          link.addEventListener('click', handleProductClick);
        });
        setIsProductClickHandlerAttached(true);
      }
    }
    return () => {
      if (isProductClickHandlerAttached) {
        const productLinksSelector = `[data-product-id="${product.id}"] a[href*="${product.id}"]`;
        const productLinks = document.querySelectorAll(productLinksSelector);

        if (productLinks.length) {
          productLinks.forEach((link) => {
            link.removeEventListener('click', handleProductClick);
          });
          setIsProductClickHandlerAttached(false);
        }
      }
    };
  });

  useEffect(() => {
    if (beacon) {
      const redirectSubstring = '&redirect=';
      let onClickBeacon = beacon;

      if (onClickBeacon.endsWith(redirectSubstring)) {
        onClickBeacon = onClickBeacon.slice(0, -redirectSubstring.length);
      }

      setAtcBeacon(onClickBeacon);
    }

  }, [beacon]);

  useEffect(() => {
    if (!atcButton) {
      const atcButtonSelector = `[data-product-id="${product.id}"] button[class*="bttn-outline--primary"]`;
      setAtcButton(document.querySelector(atcButtonSelector));
    }
  });

  useEffect(() => {
    if (atcButton && atcBeacon) {
      const atcButtonClickHandler = () => fetch(atcBeacon);

      if (!isATCButtonCLickHandlerAttached) {
        atcButton.addEventListener('click', atcButtonClickHandler);
        setIsATCButtonCLickHandlerAttached(true);
      }
    }
  }, [product, atcBeacon, atcButton]);

  useEffect(() => {
    const addBeaconingOnATCButtonClick = () => {
      const atcBtn = document.querySelector(`[data-product-id="${product.id}"] button[class*="bttn-outline--primary"]`);
      if (!isATCButtonCLickHandlerAttached && atcBtn) {
        atcBtn.addEventListener('click', () => fetch(atcBeacon));
        setIsATCButtonCLickHandlerAttached(true);
      }
    };
    window.addEventListener('load', addBeaconingOnATCButtonClick);
    return () => window.removeEventListener('load', addBeaconingOnATCButtonClick);
  }, [product.id, atcBeacon, isATCButtonCLickHandlerAttached]);

  return (
    <div
      data-product-id={product.id}
      data-onview-beacon-nucleus={product.onViewBeacon}
      data-onclick-beacon={product.onClickBeacon}
      className="sponsored-product-pod"
      data-testid="sponsored-product-pod"
    >
      <ProductPod
        itemId={product.id}
        storeId={storeId}
        analyticsData={{ parent: 'sponsored-carousel', position }}
        render={(pod) => (
          <>
            <ProductImage
              itemId={pod.itemId}
              showSecondaryImage={pod.showSecondaryImage}
            />
            <PodSection columnAlign alignTop>
              <div style={{ maxHeight: '68px' }}>
                <PodSpacer padding={['TOP', 'BOTTOM']} minHeight="68px">
                  <ProductHeader
                    brand="above"
                    itemId={pod.itemId}
                    brandTitleMaxLine={3}
                    disableShopThisCollection
                  />
                </PodSpacer>
              </div>
              <PodSpacer minHeight="26px">
                <ProductRating itemId={pod.itemId} />
              </PodSpacer>
              <div style={{ maxHeight: '60px' }}>
                <PodSpacer minHeight="60px">
                  <Price
                    itemId={pod.itemId}
                    large={false}
                    storeId={storeId}
                    displayEachUom={false}
                    onSavingsCenterToggle={pod.onSavingsCenterToggle}
                    hideBadge
                  />
                </PodSpacer>
              </div>
            </PodSection>
            {!isMobile && (
              <PodFooter>
                <PodSpacer minHeight="40px" padding={['TOP']}>
                  <ProductATC
                    itemId={pod.itemId}
                    storeId={storeId}
                    checkGeneric
                    outline
                    doNotShowChooseYourOptions
                    noATCFulfillment={noATCFulfillment}
                  />
                </PodSpacer>
              </PodFooter>
            )}
          </>
        )}
      />
    </div>
  );
};

SponsoredProductPod.propTypes = {
  storeId: string.isRequired,
  product: shape({
    id: string.isRequired,
    onClickBeacon: string.isRequired,
    onViewBeacon: string.isRequired
  }).isRequired,
  position: number.isRequired,
  schema: string.isRequired,
  pageId: string.isRequired,
  noATCFulfillment: bool,
  targetingSource: string,
};

SponsoredProductPod.defaultProps = {
  noATCFulfillment: null,
  targetingSource: 'none',
};

SponsoredProductPod.dataModel = extend(
  ProductImage,
  Price,
  ProductATC,
  ProductRating,
  ProductHeader,
  ProductPod
);

export { SponsoredProductPod };
